/**
 * @file sync.js
 * @location src/integrations/supabase/sync.js
 * @role 액션 타입 → 서버 동기화 핸들러 선언. useSupabaseStore 의 wrappedAct 가 dispatch 분기에 사용.
 *
 * ACTION_HANDLERS 한 군데에 mode + rpc + (authoritative 한정) reduce 가 모임. 새 액션 추가 시 한 줄.
 *
 *  mode: 'optimistic'    — 로컬 act() 먼저, RPC 백그라운드. 실패 시 rollback() 이 있으면 정밀 역동작,
 *                          없으면 호출 측이 refetchWorkspace 로 재동기화.
 *        'authoritative' — RPC 먼저, 결과를 reduce() 로 dispatch. 서버 발행 값(invite_code 등) 보장.
 *  매핑 없는 액션은 로컬 전용(nav/ui 등).
 *
 *  optimistic 액션의 id 는 클라이언트에서 통제(낙관적 row 와 서버 row id 일치) → ADD 류만 정밀 rollback 가능.
 *
 *  ── 액션 3분류 카탈로그 (LLM·기여자용) ──
 *  ★ 이 파일(ACTION_HANDLERS)에 등록된 액션만 서버로 나간다. 등록 = 동기화 대상.
 *
 *   1) optimistic    — 로컬 먼저 + RPC 백그라운드. 서버 영속 데이터 일반 변경
 *                      (task/template/member CRUD·핸드셰이크: ADD/EDIT/TOGGLE/ACK/CANCEL/DELETE/RESTORE/PURGE_TASK 등).
 *   2) authoritative — RPC 먼저 + 결과 reduce. 서버 발행 값 필요(ADD_MEMBER/REISSUE_INVITE 의 invite_code)
 *                      또는 다중 row 동시 갱신(TRANSFER_MANAGER — reduce 없이 refetch 보정).
 *   3) local-only    — 여기 미등록 = 서버에 안 나감. UI/nav/transient(SET_NAV·SET_UI·SET_UI_PREF·TOGGLE_DARK·
 *                      SET_SECTION_OPEN·RESET_DATA·LOAD_STATE) + 부팅 housekeeping(AUTO_GENERATE_RECURRING·
 *                      AUTO_CARRY_OVER·AUTO_PURGE_TRASH — 온라인은 서버 pg_cron 권위, 클라는 오프라인 폴백) + 인프라 큐(OUTBOX_*).
 *  ※ HYDRATE·SYNC_*(syncActions.js)는 사용자 액션이 아니라 realtime/하이드레이션 수신 전용 — act() 경유 아님.
 *
 *  ▶ 새 액션 추가 결정 규칙: "서버에 영속되는 데이터를 바꾸나?"
 *      YES → 여기 등록(기본 optimistic, 서버 발행/다중행이면 authoritative).
 *      NO(로컬 UI/transient) → 등록하지 않음(local-only). 등록 누락 = 동기화 깜빡 버그.
 */
import * as tasksApi from './api/tasks.js';
import * as tmplApi from './api/templates.js';
import * as membersApi from './api/members.js';

/** @typedef {{ mode: 'optimistic' | 'authoritative', rpc: (p: object) => Promise<any>, reduce?: (result: any, p: object) => object | null, rollback?: (p: object) => object | null }} ActionHandler */

/** @type {Record<string, ActionHandler>} */
export const ACTION_HANDLERS = {
  // ── tasks (낙관적) ──
  ADD_TASK: {
    mode: 'optimistic',
    rpc: (p) => tasksApi.assignTask(p.task),
    rollback: (p) => ({ type: 'SYNC_TASK_REMOVE', id: p.task.id }),
  },
  EDIT_TASK:     { mode: 'optimistic', rpc: (p) => tasksApi.editTask(p.id, p.changes) },
  ACK_TASK:      { mode: 'optimistic', rpc: (p) => tasksApi.acknowledgeTask(p.id) },
  TOGGLE_TASK:   { mode: 'optimistic', rpc: (p) => tasksApi.toggleTask(p.id) },
  CANCEL_TASK:   { mode: 'optimistic', rpc: (p) => tasksApi.cancelTask(p.id, p.reason) },
  DELETE_TASK:   { mode: 'optimistic', rpc: (p) => tasksApi.trashTask(p.id) },
  RESTORE_TASK:  { mode: 'optimistic', rpc: (p) => tasksApi.restoreTask(p.id ?? p.task?.id) },
  PURGE_TASK:    { mode: 'optimistic', rpc: (p) => tasksApi.purgeTask(p.id) },

  // ── templates (낙관적) ──
  ADD_TEMPLATE: {
    mode: 'optimistic',
    rpc: (p) => tmplApi.addTemplate(p.template),
    rollback: (p) => ({ type: 'SYNC_TEMPLATE_REMOVE', id: p.template.id }),
  },
  EDIT_TEMPLATE:   { mode: 'optimistic', rpc: (p) => tmplApi.editTemplate(p.id, p.patch) },
  TOGGLE_TEMPLATE: { mode: 'optimistic', rpc: (p) => tmplApi.toggleTemplate(p.id) },
  DELETE_TEMPLATE: { mode: 'optimistic', rpc: (p) => tmplApi.deleteTemplate(p.id) },

  // ── members (낙관적: id 가 이미 알려진 경우) ──
  EDIT_MEMBER:    { mode: 'optimistic', rpc: (p) => membersApi.editMember(p.id, p.changes) },
  DELETE_MEMBER:  { mode: 'optimistic', rpc: (p) => membersApi.deleteMember(p.id) },
  UPDATE_PROFILE: { mode: 'optimistic', rpc: (p) => membersApi.updateProfile(p.name) },

  // ── members (권위: 서버가 invite_code 발행) ──
  ADD_MEMBER: {
    mode: 'authoritative',
    rpc: (p) => membersApi.createInvite(p.member?.name ?? ''),
    reduce: (member) => member ? { type: 'SYNC_MEMBER_UPSERT', member } : null,
  },
  REISSUE_INVITE: {
    mode: 'authoritative',
    rpc: (p) => membersApi.reissueInvite(p.id),
    reduce: (member) => member ? { type: 'SYNC_MEMBER_UPSERT', member } : null,
  },
  TRANSFER_MANAGER: {
    mode: 'authoritative',
    rpc: (p) => membersApi.transferManager(p.targetId),
    // reduce 없음 — 두 멤버 동시 갱신은 단일 reduce 로 불가. 호출 측이 refetchWorkspace 로 보정.
  },
};
